import type { SerializedEditorState, SerializedLexicalNode } from './lexical-types';

/**
 * Pure helpers for a stored rich-text value. New content is a Lexical editor state serialized to
 * JSON; legacy content is plain text. These helpers detect which one a value is and derive display
 * info, with no editor/DOM dependency. Turning a value into live editor content is done separately
 * in `rich-text-apply.ts` (which needs Lexical).
 *
 * Ported from the Gold app (cashpile/apps/cashvest) so both apps interpret the shared S3 comment
 * format identically.
 */
export type StoredContent =
  | { kind: 'json'; state: SerializedEditorState }
  | { kind: 'text'; text: string };

function isSerializedEditorState(value: unknown): value is SerializedEditorState {
  return (
    typeof value === 'object' &&
    value !== null &&
    'root' in value &&
    typeof (value as { root: unknown }).root === 'object'
  );
}

/**
 * Classify a stored value. A value that parses as JSON with a Lexical `root` is treated as an
 * editor state; anything else (including empty string and markdown/plain text) is plain text.
 */
export function parseStoredContent(value: string): StoredContent {
  const trimmed = value.trim();
  if (trimmed.startsWith('{')) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (isSerializedEditorState(parsed)) {
        return { kind: 'json', state: parsed };
      }
    } catch {
      // Not JSON — fall through to plain text.
    }
  }
  return { kind: 'text', text: value };
}

type NodeWithChildren = SerializedLexicalNode & {
  text?: string;
  children?: SerializedLexicalNode[];
};

/** Concatenate the visible text of a serialized Lexical state (used for empty-state detection). */
export function extractPlainText(state: SerializedEditorState): string {
  const parts: string[] = [];
  const walk = (node: NodeWithChildren) => {
    if (typeof node.text === 'string') parts.push(node.text);
    if (Array.isArray(node.children)) {
      for (const child of node.children) walk(child as NodeWithChildren);
    }
  };
  walk(state.root as unknown as NodeWithChildren);
  return parts.join('');
}

/** True when a stored value has any visible text — for both JSON and legacy plain-text values. */
export function storedContentHasText(value: string): boolean {
  const parsed = parseStoredContent(value);
  return parsed.kind === 'json'
    ? extractPlainText(parsed.state).trim() !== ''
    : parsed.text.trim() !== '';
}
